import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';

import { AppManager, Input } from 'core';
import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';

import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';
import { CreateSharedFileMutation } from 'apps/share/gql';

import { FormStore } from 'libs/forms/app';
import { getExtensions } from 'libs/rendering/utils';
// FIXME - move to modal registry once that PR is merged
import { ConfirmSubscription } from 'apps/payment/subscription/confirmModal';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { SharedFileModel } from 'apps/share/app';

const formConfig = {
  shareFile: {
    fields: ['sharedFile'],
  },
  fields: {
    sharedFile: {
      label: 'File',
      component: 'forms.FileField',
      optional: false,
      accept: getExtensions('GltfViewer')
        .map(extension => `.${extension}`)
        .join(','),
    },
  },
};

export default class UploadSharedFileAppManager extends AppManager {
  static registryName = 'UploadSharedFileAppManager';
  @observable inSpace = null;
  @observable droppedFile = null;
  subscriptionManager = null;

  constructor(parent, init = {}) {
    super(parent, init);
    this.inSpace = clientStore.signedInProfile?.inSpace;
    if (!this.inSpace) this.inSpace = { id: 'dummyId' };
    this.form = new FormStore(this, formConfig, 'shareFile');
  }

  @computed get hasDroppedFile() {
    return !!this.droppedFile;
  }

  @action onSharedFileCreateSuccess = sharedFile => {
    analytics.sendEvent(
      'completed-shared-file',
      {},
      { ga: true, intercom: true },
    );
    analytics.sendEvent(
      'Contact',
      { currency: 'EUR', value: 12 },
      {
        facebook: true,
      },
    );

    this.droppedFile = null;

    if (routerStore.route.name.includes('make-part')) {
      routerStore.navigate('make-part.file', {
        sharedFileId: sharedFile.id,
        panel: 'rfq',
      });
    } else {
      const sharedFileModel = SharedFileModel.Load(sharedFile);
      const { path, spaceType } = sharedFileModel.inSpace.parent;

      const params = {};
      let routeName = '';
      if (spaceType === 'PROFILE') {
        routeName = 'profile';
        params.profile = path;
      } else if (spaceType === 'INITIATIVE') {
        routeName = 'initiative';
        params.initiative = path;
      }

      routerStore.router.navigate(`${routeName}.shared.file`, {
        ...params,
        sharedFileId: sharedFile.id,
      });
    }
  };

  @action onSharedFileError = error => {
    this.droppedFile = null;
    Alert.error('Whoops! There was an error sharing the file');
    analytics.logError(error);
  };

  @action onUploadSuccess = () => {
    const fieldStore = this.form.fieldStores.get('sharedFile');
    const fileId = fieldStore.fileAppManager.file?.id;
    const input = new Input({
      fileId,
      targetSpaceSlug: () => clientStore.activeSpace?.path,
      private: () => true,
    });
    const options = {
      onSuccess: this.onSharedFileCreateSuccess,
      onUserErr: this.onSharedFileError,
      onSysErr: this.onSharedFileError,
    };
    const headers = {};
    if (!clientStore.signedInProfile && clientStore.HMAC) {
      headers['X-Wikifactory-Hmac'] = clientStore.HMAC;
    }
    CreateSharedFileMutation.New(input, options, headers);
  };

  @action HandleUpgradePlan = () => {
    const sharedFileSpace = clientStore.signedInProfile?.inSpace;

    this.subscriptionManager = new SubscriptionAppManager(this, {
      spaceSlug: sharedFileSpace.path,
    });
    this.subscriptionManager.NewSubscriptionQuery({ preview: true });
    modalStore.SetModalComponent(ConfirmSubscription, {
      appManager: this.subscriptionManager,
      confirmAction: () => {
        this.subscriptionManager.paymentSpacePreview.SetSubscribing(true);
        return this.subscriptionManager.paymentSpacePreview.Subscribe({
          onSuccess: () => {
            this.subscriptionManager.paymentSpacePreview.SetSubscribing(false);
            modalStore.Close();
            this.processDroppedFile();
          },
          onSysError: this.subscriptionManager.paymentSpacePreview
            .OnSubscribeError,
          onUserError: this.subscriptionManager.paymentSpacePreview
            .OnSubscribeError,
        });
      },
    });
  };

  @action HandleFileDrop = accepted => {
    // Save reference to accepted file in order to use it after subscription preview
    [this.droppedFile] = accepted;
    // FIXME - connect to space selector once that's available
    /* Removed because of WF2.0 billing policy
    const sharedFileSpace = clientStore.signedInProfile?.inSpace;
    if (
      !(
        sharedFileSpace.hasPaidSubscription ||
        sharedFileSpace.remainingSharedFiles
      )
    ) {
      if (sharedFileSpace.hasBillingPermissions) {
        this.HandleUpgradePlan();
      } else {
        this.droppedFile = null;
        modalStore.SetModalComponent(RequiresBillingModal);
      }
    } else {
      this.processDroppedFile();
    } */
    this.processDroppedFile();
  };

  @action processDroppedFile = () => {
    const fieldStore = this.form.fieldStores.get('sharedFile');
    this.droppedFile.fileObject.private = true;
    fieldStore.Drop([this.droppedFile], [], true, this.onUploadSuccess);
  };
}
